/**
 * Signals: how a diagnostic finding becomes, and stops being, an incident.
 *
 * ADR-49 built the eyes (findings with fingerprints and evidence); ADR-58 cut 2
 * gives them a memory. A signal is one fingerprint observed over consecutive
 * sweeps. Design doc section 28 is the rule, and it is a rule about NOT
 * flapping: one failure is recorded, three consecutive failures open an
 * incident, three consecutive clean sweeps move it to MONITORING, five close
 * it. Every threshold is a parameter, none is a magic number in the sweep.
 *
 * `stepSignal` is pure: previous state plus "was it observed this sweep" in,
 * next state plus the one action the sweep must take out. The sweep does the
 * I/O; this file decides. That is what makes the flapping rule testable
 * without a database and readable without the sweep.
 */

import type { Severity } from '@hipnumber/diagnostics-contract';
import type { IncidentSeverity } from './incident.js';

export const SIGNAL_STATUSES = ['OBSERVING', 'OPEN', 'CLEARING', 'CLEARED'] as const;
export type SignalStatus = (typeof SIGNAL_STATUSES)[number];

export interface SignalThresholds {
  /** Consecutive sweeps a finding must be seen before an incident is opened. */
  readonly openAfter: number;
  /** Consecutive clean sweeps before an automation-owned incident goes to MONITORING. */
  readonly monitorAfter: number;
  /** Consecutive clean sweeps before an automation-owned incident is RESOLVED. Must exceed monitorAfter. */
  readonly resolveAfter: number;
}

export const DEFAULT_SIGNAL_THRESHOLDS: SignalThresholds = { openAfter: 3, monitorAfter: 3, resolveAfter: 5 };

export interface SignalState {
  readonly status: SignalStatus;
  readonly consecutiveSeen: number;
  readonly consecutiveClear: number;
}

/**
 * What the sweep does after a step:
 *   NONE     persist the counters, nothing else
 *   OPEN     the threshold was reached: open (or attach to) an incident
 *   REOPEN   it was clearing and came back: note it, and un-monitor an automation-owned incident
 *   MONITOR  clean for monitorAfter sweeps: note it, move an automation-owned incident to MONITORING
 *   RESOLVE  clean for resolveAfter sweeps: resolve an automation-owned incident whose signals are all clear
 *   FORGET   a blip that never reached the threshold: drop the row
 */
export type SignalAction = 'NONE' | 'OPEN' | 'REOPEN' | 'MONITOR' | 'RESOLVE' | 'FORGET';

export function stepSignal(prev: SignalState | null, observed: boolean, t: SignalThresholds = DEFAULT_SIGNAL_THRESHOLDS): { state: SignalState; action: SignalAction } {
  if (observed) {
    if (prev === null || prev.status === 'OBSERVING' || prev.status === 'CLEARED') {
      const seen = prev?.status === 'OBSERVING' ? prev.consecutiveSeen + 1 : 1;
      if (seen >= t.openAfter) return { state: { status: 'OPEN', consecutiveSeen: seen, consecutiveClear: 0 }, action: 'OPEN' };
      return { state: { status: 'OBSERVING', consecutiveSeen: seen, consecutiveClear: 0 }, action: 'NONE' };
    }
    if (prev.status === 'CLEARING') return { state: { status: 'OPEN', consecutiveSeen: prev.consecutiveSeen + 1, consecutiveClear: 0 }, action: 'REOPEN' };
    return { state: { status: 'OPEN', consecutiveSeen: prev.consecutiveSeen + 1, consecutiveClear: 0 }, action: 'NONE' };
  }
  if (prev === null) return { state: { status: 'CLEARED', consecutiveSeen: 0, consecutiveClear: 0 }, action: 'FORGET' };
  switch (prev.status) {
    case 'OBSERVING': return { state: { ...prev, consecutiveSeen: 0 }, action: 'FORGET' };
    case 'CLEARED': return { state: prev, action: 'NONE' };
    case 'OPEN':
    case 'CLEARING': {
      const clear = prev.consecutiveClear + 1;
      if (clear >= t.resolveAfter) return { state: { status: 'CLEARED', consecutiveSeen: prev.consecutiveSeen, consecutiveClear: clear }, action: 'RESOLVE' };
      if (clear === t.monitorAfter) return { state: { status: 'CLEARING', consecutiveSeen: prev.consecutiveSeen, consecutiveClear: clear }, action: 'MONITOR' };
      return { state: { status: 'CLEARING', consecutiveSeen: prev.consecutiveSeen, consecutiveClear: clear }, action: 'NONE' };
    }
  }
}

/**
 * Severity for an automatically opened incident (design doc sections 73, 74):
 * the same finding is worse when a sale is live and worse again when it is
 * the whole platform. Dynamic re-scoring as auctions go live is a later cut;
 * this is the opening call, and an operator can change it.
 */
export function severityForSignal(finding: Severity, ctx: { liveSale: boolean; platformWide: boolean }): IncidentSeverity {
  if (finding === 'error') return ctx.platformWide ? 'SEV1' : ctx.liveSale ? 'SEV2' : 'SEV3';
  if (finding === 'warning') return ctx.liveSale ? 'SEV3' : 'SEV4';
  return 'SEV4';
}

/** Which internal component a finding code implicates, for the impact row. Null when it names none. */
export function serviceForFindingCode(code: string): string | null {
  if (code.startsWith('PROBE_POSTGRES')) return 'DATABASE';
  // ADR-60 cut 8: the synthetic canary exercises bidding end to end.
  if (code.startsWith('PROBE_SYNTHETIC_CANARY')) return 'BID_ENGINE';
  if (code.startsWith('PROBE_RING_OBSERVER') || code.startsWith('EVENT_SEQ')) return 'EVENT_STORE';
  if (code.startsWith('PROBE_STOREFRONT')) return 'STOREFRONT';
  // ADR-65 cut 0: a second sale of a standing lot stops the projector on purpose.
  if (code.startsWith('PROJECTOR') || code.startsWith('FINALIZED') || code.startsWith('SOLD_LOT') || code.startsWith('STANDING_SALES')) return 'SETTLEMENT';
  if (code.startsWith('RING_') || code.startsWith('CURRENT_LOT')) return 'BID_ENGINE';
  // ADR-60 cut 4: the live runtime's own signals.
  if (code === 'OPERATOR_OFFLINE' || code === 'PENDING_BID_STALE' || code === 'BID_LATENCY_HIGH' || code === 'REALTIME_DISCONNECT_SPIKE' || code === 'STATE_SEQUENCE_GAP' || code === 'RUNTIME_TELEMETRY_UNOBSERVABLE') return 'BID_ENGINE';
  // ADR-60 cut 6: the media plane, which is never the bid engine.
  if (code.startsWith('MEDIA_')) return 'MEDIA';
  if (code.startsWith('INVOICES') || code.startsWith('PAYMENT') || code.startsWith('PROVIDER_EVENTS')) return 'PAYMENTS';
  return null;
}

/** The actor every automation-written timeline row names. */
export const SWEEP_ACTOR = 'ops-sweep';
